import { Search } from "baseui/icon";
import classNames from "classnames";
import React, { Dispatch, FC, SetStateAction, useMemo, useState } from "react";
import { HiXMark } from "react-icons/hi2";
import { useDebounce } from "usehooks-ts";

import Button from "../../components/button/Button";
import CustomSelect from "../../components/select/Select";
import { getInitialsFromFullName } from "../../constants/constants";
import { TimePeriod } from "../../hooks/useDetectTimePeriod";
import { useAppSelector } from "../../redux/redux";
import { getLinkedFirm } from "../../sessionStorage/sessionStorage";
import { useGetSearchCustomersQuery } from "../../slices/ContactsSlice";
import { useGetUsersQuery } from "../../slices/UserSlice";
import { useGetWorkTypesQuery } from "../../slices/WorkTypeSlice";
import { SelectOptionType } from "../../types/types";
import { SelectDate } from "../billing/SelectDate";
import { Time } from "./InvoiceTime";
import { createDefaultTimeFilter } from "./time-filter.utils";

export enum TimeFilterKey {
  Date = "date",
  WorkType = "workType",
  Assignee = "assignee",
  Client = "client",
  Status = "status",
}

export const DEFAULT_VISIBLE_TIME_FILTERS: TimeFilterKey[] = [
  TimeFilterKey.Date,
  TimeFilterKey.WorkType,
  TimeFilterKey.Assignee,
  TimeFilterKey.Client,
  TimeFilterKey.Status,
];

type Props = {
  filter: Time;
  setFilter: Dispatch<SetStateAction<Time>>;
  visibleFilters?: TimeFilterKey[];
  presetFilters?: Partial<Time>;
};

const STATUS_OPTIONS: SelectOptionType[] = [
  { value: "Billed", label: "Billed" },
  { value: "Saved", label: "Unbilled" },
  { value: "NonBillable", label: "Non-billable" },
];

export const TimeFilters: FC<Props> = ({
  filter,
  setFilter,
  visibleFilters = DEFAULT_VISIBLE_TIME_FILTERS,
  presetFilters,
}) => {
  const [searchClients, setSearchClients] = useState("");
  const [searchWorkTypes, setSearchWorkTypes] = useState("");

  const { user } = useAppSelector((state) => state.appReducer);
  const orgId = getLinkedFirm()?.orgId || "";

  const showDate = visibleFilters.includes(TimeFilterKey.Date);
  const showWorkType = visibleFilters.includes(TimeFilterKey.WorkType);
  const showAssignee = visibleFilters.includes(TimeFilterKey.Assignee);
  const showClient = visibleFilters.includes(TimeFilterKey.Client);
  const showStatus = visibleFilters.includes(TimeFilterKey.Status);

  const debouncedSearchClient = useDebounce(searchClients, 50);

  const { data: workTypes, isFetching: isLoadingWorkTypes } =
    useGetWorkTypesQuery({ orgId }, { skip: !orgId || !showWorkType });

  const { data: users, isFetching: isLoadingUsers } = useGetUsersQuery(orgId, {
    skip: !orgId || !showAssignee,
  });

  const { data: contacts, isFetching: isLoadingContacts } =
    useGetSearchCustomersQuery(
      {
        searchQuery: debouncedSearchClient || "ab",
        orgId,
        userId: user.id,
        isActive: true,
      },
      { skip: !orgId || !user.id || !showClient },
    );

  const usersOptions = useMemo<SelectOptionType[]>(() => {
    return (
      users?.map((item) => ({
        avatar: getInitialsFromFullName(
          `${item?.userProfile?.firstName} ${item?.userProfile?.lastName}`,
        ),
        label: `${item?.userProfile?.firstName} ${item?.userProfile?.lastName}`,
        value: `${item?.id}`,
        avatarCustomColor: item?.userProfile?.color,
        type: "custom",
      })) || []
    );
  }, [users]);

  const workTypeOptions = useMemo<SelectOptionType[]>(() => {
    const normalizedSearch = searchWorkTypes.trim().toLowerCase();

    const options =
      workTypes?.map((item) => ({
        label: item.name,
        value: item.id,
      })) || [];

    if (!normalizedSearch) {
      return options;
    }

    return options.filter((item) =>
      item.label.toLowerCase().includes(normalizedSearch),
    );
  }, [workTypes, searchWorkTypes]);

  const clientOptions = useMemo<SelectOptionType[]>(() => {
    return (
      contacts?.map((contact) => ({
        value: contact?.customerId,
        label: `${contact.customerName}`,
        avatar: getInitialsFromFullName(contact?.customerName),
        avatarColor: contact.businessContactId ? "purple" : "blue",
      })) || []
    );
  }, [contacts]);

  const selectedStatus = useMemo<SelectOptionType | null>(() => {
    if (filter.filterByBillable === false) {
      return STATUS_OPTIONS[2];
    }

    if (filter.filterByStatus === true) {
      return STATUS_OPTIONS[0];
    }

    if (filter.filterByStatus === false) {
      return STATUS_OPTIONS[1];
    }

    return null;
  }, [filter.filterByStatus, filter.filterByBillable]);

  const clearValues = () => {
    setFilter(
      createDefaultTimeFilter({
        orgId,
        presetFilters,
      }),
    );
    setSearchClients("");
    setSearchWorkTypes("");
  };

  const isDateDirty =
    showDate &&
    (filter.filterByDate[0] !== TimePeriod.LastYear ||
      filter.fromDate !== null ||
      filter.toDate !== null);

  const isWorkTypeDirty = showWorkType && !!filter.filterByWorkType.length;

  const isAssigneeDirty = showAssignee && !!filter.filterByAssignee.length;

  const isClientDirty = showClient && !!filter.filterByClient.length;

  const isStatusDirty =
    showStatus &&
    (filter.filterByStatus !== null || filter.filterByBillable === false);

  const isShowClearButton =
    isDateDirty ||
    isWorkTypeDirty ||
    isAssigneeDirty ||
    isClientDirty ||
    isStatusDirty;

  return (
    <div className={"flex items-start gap-4 max-md:flex-wrap"}>
      <div
        className={classNames(
          "grid grid-cols-5 gap-x-4 gap-y-2",
          "max-[1780px]:grid-cols-3 max-[1600px]:grid-cols-2",
          "max-md:w-full max-md:grid-cols-1",
        )}>
        {showDate && (
          <div className={"min-w-[200px]"}>
            <div className={"flex flex-col"}>
              <div className={"mb-0.5 text-sm"}>Date</div>
              <div>
                <SelectDate
                  hasAll={false}
                  small
                  setValue={(value) =>
                    setFilter((prev) => ({
                      ...prev,
                      filterByDate: value.period ? [value.period] : [],
                      fromDate: value.from ?? null,
                      toDate: value.to ?? null,
                    }))
                  }
                  value={{
                    to: filter.toDate,
                    from: filter.fromDate,
                    period: filter.filterByDate[0] ?? null,
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {showWorkType && (
          <div className={"min-w-[200px]"}>
            <div className={"mb-0.5 text-sm"}>Work type</div>
            <CustomSelect
              options={workTypeOptions}
              value={filter.filterByWorkType}
              onChange={(newValue) => {
                const value = newValue as SelectOptionType[];
                setFilter((prev) => ({
                  ...prev,
                  filterByWorkType: value,
                }));
              }}
              inputValue={searchWorkTypes}
              onChangeInput={setSearchWorkTypes}
              isMulti
              withMenu
              isLoading={isLoadingWorkTypes}
              placeholder={"All"}
              selectStyle={{ fitChips: true, small: true }}
            />
          </div>
        )}

        {showAssignee && (
          <div className={"min-w-[200px]"}>
            <div className={"mb-0.5 text-sm"}>Assignee</div>
            <CustomSelect
              options={usersOptions}
              value={filter.filterByAssignee}
              onChange={(newValue) => {
                const value = newValue as SelectOptionType[];
                setFilter((prev) => ({
                  ...prev,
                  filterByAssignee: value,
                }));
              }}
              isMulti
              withMenu
              isLoading={isLoadingUsers}
              selectStyle={{ fitChips: true, small: true }}
            />
          </div>
        )}

        {showClient && (
          <div className={"min-w-[200px]"}>
            <div className={"mb-0.5 text-sm"}>Client</div>
            <CustomSelect
              options={clientOptions}
              value={filter.filterByClient}
              onChange={(newValue) => {
                const value = newValue as SelectOptionType[];
                setFilter((prev) => ({
                  ...prev,
                  filterByClient: value,
                }));
              }}
              inputValue={searchClients}
              onChangeInput={setSearchClients}
              isMulti
              isLoading={isLoadingContacts}
              indicatorIcon={() => <Search size={24} />}
              selectStyle={{ countLabel: "clients", small: true }}
              placeholder={"Search"}
            />
          </div>
        )}

        {showStatus && (
          <div className={"min-w-[200px]"}>
            <div className={"mb-0.5 text-sm"}>Status</div>
            <CustomSelect
              options={STATUS_OPTIONS}
              withMenu
              value={selectedStatus}
              onChange={(newValue) => {
                const value = newValue as SelectOptionType | null;

                // Unbilled = billable time not on an invoice yet.
                setFilter((prev) => ({
                  ...prev,
                  filterByStatus:
                    value?.value === "Billed"
                      ? true
                      : value?.value === "Saved"
                      ? false
                      : null,
                  filterByBillable:
                    value?.value === "Saved"
                      ? true
                      : value?.value === "NonBillable"
                      ? false
                      : null,
                }));
              }}
              selectStyle={{ small: true }}
            />
          </div>
        )}
      </div>
      {isShowClearButton && (
        <div className={"mt-3"}>
          <Button
            label={"Clear"}
            onClick={clearValues}
            colorType={"ghost"}
            icon={<HiXMark size={20} />}
          />
        </div>
      )}
    </div>
  );
};
